'use client'

import { useEffect, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { navigateUrl } from '@/utils/client-request'
import { isCustomerAuthActive } from '@/utils/util'
import { useAuth } from './useAuth'

export type NavIconMap = {
  catalog?: any
  campaign?: any
  featured?: any
  new?: any
  sale?: any
  brand?: any
  blog?: any
  gift?: any
  loyalty?: any
}

export type SocialPlatform = 'facebook' | 'instagram' | 'twitter' | 'youtube'

export type FooterSocial = {
  platform: SocialPlatform
  name: string
  href: string
}

type Args = {
  vendor: any
  subscription?: any
  campaigns?: any[]
  categories?: any[]
  icons?: NavIconMap
}

const formatSocialUrl = (url: string) => {
  if (!url) return '#'
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  return `https://${url}`
}

const buildSocials = (vendor: any): FooterSocial[] => {
  if (!vendor?.socials) return []
  let socials: any = vendor.socials
  if (typeof socials === 'string') {
    try { socials = JSON.parse(socials) } catch { return [] }
  }
  const list: FooterSocial[] = []
  if (socials.facebook) list.push({ platform: 'facebook', name: 'Facebook', href: formatSocialUrl(socials.facebook) })
  if (socials.instagram) list.push({ platform: 'instagram', name: 'Instagram', href: formatSocialUrl(socials.instagram) })
  if (socials.twitter) list.push({ platform: 'twitter', name: 'X', href: formatSocialUrl(socials.twitter) })
  if (socials.youtube) list.push({ platform: 'youtube', name: 'YouTube', href: formatSocialUrl(socials.youtube) })
  return list
}

const buildCategorySections = (categories: any[], pathname: string) => {
  return categories
    .sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0))
    .filter((category: any) => category.level === 0)
    .map((category: any) => ({
      id: category.id,
      name: category.name,
      href: navigateUrl(`/product?category=${category.slug || category.id}`, pathname),
      items: (category.children || [])
        .sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0))
        .map((child: any) => ({
          name: child.name,
          href: navigateUrl(`/product?category=${child.slug || child.id}`, pathname),
          children: (child.children || [])
            .sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0))
            .map((grandChild: any) => ({
              name: grandChild.name,
              href: navigateUrl(`/product?category=${grandChild.slug || grandChild.id}`, pathname),
            })),
        })),
    }))
}

export function useNavigation({ vendor, subscription, campaigns, categories, icons = {} }: Args) {
  const { session } = useAuth()
  const pathname = usePathname()
  const [activeParentCategory, setActiveParentCategory] = useState<any>(null)
  const [hoveredParentCategory, setHoveredParentCategory] = useState<any>(null)

  const activeCampaigns = useMemo(() =>
    (campaigns || []).filter((c: any) => c.havePage && c.handle && (!c.customerOnly || !!session?.user?.id)),
    [campaigns, session]
  )

  const navigation = useMemo(() => {
    const sections = categories && categories.length > 0 ? buildCategorySections(categories, pathname || '') : []
    return {
      catalog: {
        id: 'catalog',
        name: 'Ангилал',
        current: false,
        icon: icons.catalog,
        featured: [] as any,
        sections,
        highlights: [
          { name: 'Онцлох', href: '/product?category=featured', icon: icons.featured, isHighlight: true },
          { name: 'Шинэ ирсэн', href: '/product?category=new', icon: icons.new, isHighlight: true },
          { name: 'Хямдрал', href: '/product?category=sale', icon: icons.sale, isSale: true, isHighlight: true },
        ],
      },
      links: [
        ...activeCampaigns.map((c: any) => ({ name: c.name, href: `/c/${c.handle}`, icon: icons.campaign, isCampaign: true })),
        { name: 'Онцлох', href: '/product?category=featured', icon: icons.featured, isHighlight: true },
        { name: 'Шинэ ирсэн', href: '/product?category=new', icon: icons.new, isHighlight: true },
        { name: 'Хямдрал', href: '/product?category=sale', icon: icons.sale, isSale: true, isHighlight: true },
        ...(vendor?.modules?.includes('brand') ? [{ name: 'Брэнд', href: '/brand', icon: icons.brand }] : []),
        ...(subscription?.canUseBlog && vendor?.modules?.includes('blog') ? [{ name: 'Нийтлэл', href: '/blog', icon: icons.blog }] : []),
        ...(subscription?.canUseGiftCard && vendor?.useGiftCard && vendor?.modules?.includes('gift-card') ? [{ name: 'Бэлгийн карт', href: '/gift', icon: icons.gift }] : []),
        ...(icons.loyalty && subscription?.canUseLoyalty && vendor?.modules?.includes('loyalty') ? [{ name: 'Лоялти', href: '/loyalty', icon: icons.loyalty }] : []),
      ],
    }
  }, [vendor, subscription, activeCampaigns, categories, pathname, icons])

  const isLoadingCategories = !categories || categories.length === 0

  useEffect(() => {
    if (!activeParentCategory && navigation.catalog.sections.length > 0) {
      setActiveParentCategory(navigation.catalog.sections[0])
    }
  }, [navigation.catalog.sections, activeParentCategory])

  const footer = useMemo(() => {
    const info = [
      ...(subscription?.canUseAboutUsPage ? [{ name: 'Бидний тухай', href: '/about' }] : []),
      ...(vendor?.modules?.includes('branch') ? [{ name: 'Салбарын мэдээлэл', href: '/branch' }] : []),
      ...(subscription?.canUseTerms ? [{ name: 'Үйлчилгээний нөхцөл', href: '/service' }] : []),
      ...(subscription?.canUseTerms ? [{ name: 'Хүргэлтийн нөхцөл', href: '/delivery' }] : []),
      ...(subscription?.canUseTerms ? [{ name: 'Төлбөрийн нөхцөл', href: '/payment' }] : []),
    ]
    const help = [
      { name: 'Холбогдох мэдээлэл', href: '/contact' },
      ...(isCustomerAuthActive(vendor) ? [{ name: 'Миний бүртгэл', href: '/profile' }] : []),
      { name: 'Сагс', href: '/cart' },
      { name: 'Захиалгын явц', href: '/order/check' },
    ]
    const socials = buildSocials(vendor)
    return { info, help, socials, hasInfoSection: info.length > 0 }
  }, [vendor, subscription])

  return {
    navigation,
    isLoadingCategories,
    activeParentCategory, setActiveParentCategory,
    hoveredParentCategory, setHoveredParentCategory,
    footer,
  }
}
